import React, {useEffect, useMemo, useState} from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Switch from '@mui/material/Switch';
import Slider from '@mui/material/Slider';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NewOccurrenceReportService from '../../../../services/new-occurrence.service';
import OccurrenceReportDescriptorService from '../../../../services/occurrence-report-descriptor.service';
import OccurrenceReportDescriptor, {
    OccurrenceReportDescriptorTerm
} from '../../../../@types/occurrence-report-descriptor';
import {toast} from 'react-toastify';
import {Box, Button, Chip, Paper, Stack, TextField, Tooltip} from '@mui/material';
import NewOccurrenceReport from '../../../../@types/new-occurrence-report';

interface WhyEdit {
    explanation: string;
    confidenceScore: string;
    supportingPhrases: string[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapResponse = (result: any[]): OccurrenceReportDescriptorTerm[] =>
    result.map(t => ({
        id: t.descriptorTermId,
        term: t.term,
        definition: t.definition,
        descriptorId: t.descriptorId,
        explanation: t.explanation,
        confidenceScore: t.confidenceScore,
        supportingPhrases: t.supportingPhrases,
    }));

interface LinkedDescriptorsEditorProps {
    occurrence: NewOccurrenceReport;
    onPhrasesHover?: (phrases: string[]) => void;
}

const NewOccurrenceReportLinkedDescriptorsEditor: React.FC<LinkedDescriptorsEditorProps> = ({ occurrence, onPhrasesHover }) => {
    const [publishedDescriptors, setPublishedDescriptors] = useState<OccurrenceReportDescriptor[]>([]);
    const [appliedTerms, setAppliedTerms] = useState<OccurrenceReportDescriptorTerm[]>([]);
    const [loading, setLoading] = useState(false);
    const [expandedSets, setExpandedSets] = useState<Set<number>>(new Set());
    const [whyOpen, setWhyOpen] = useState<Set<number>>(new Set());
    const [whyEdits, setWhyEdits] = useState<Record<number, WhyEdit>>({});
    const [phraseInputs, setPhraseInputs] = useState<Record<number, string>>({});

    useEffect(() => {
        OccurrenceReportDescriptorService.fetchPublishedDescriptors({ size: 500, offset: 0, sort: 'name', direction: 'asc' })
            .then(result => setPublishedDescriptors(result.content || []))
            .catch(() => toast.error('Failed to fetch published descriptors'));
    }, []);

    useEffect(() => {
        NewOccurrenceReportService.fetchReportDescriptorTerms(occurrence.id)
            .then(result => {
                const terms = mapResponse(result);
                setAppliedTerms(terms);
                const edits: Record<number, WhyEdit> = {};
                terms.forEach(t => {
                    edits[t.id!] = {
                        explanation: t.explanation || '',
                        confidenceScore: t.confidenceScore != null ? String(t.confidenceScore) : '',
                        supportingPhrases: t.supportingPhrases || [],
                    };
                });
                setWhyEdits(edits);
            })
            .catch(() => toast.error('Failed to fetch report descriptors'));
    }, []);

    const appliedSet = useMemo(() => new Set(appliedTerms.map(t => t.id)), [appliedTerms]);

    const appliedGroups = useMemo(() =>
        publishedDescriptors
            .map(d => ({ descriptor: d, terms: d.descriptorTerms.filter(t => appliedSet.has(t.id)) }))
            .filter(g => g.terms.length > 0),
    [publishedDescriptors, appliedSet]);

    const buildPayload = (): Array<{
        descriptorTermId: number;
        explanation?: string;
        confidenceScore?: number;
        supportingPhrases?: string[];
    }> =>
        appliedTerms.map(t => {
            const edit = whyEdits[t.id!] ?? {};
            return {
                descriptorTermId: t.id!,
                explanation: edit.explanation || undefined,
                confidenceScore: edit.confidenceScore ? parseFloat(edit.confidenceScore) : undefined,
                supportingPhrases: edit.supportingPhrases?.length > 0 ? edit.supportingPhrases : undefined,
            };
        });

    const handleApplyTerm = (termId: number): void => {
        const payload = [...buildPayload(), { descriptorTermId: termId }];
        setLoading(true);
        NewOccurrenceReportService.linkReportDescriptorTerms(occurrence.id, payload)
            .then(result => {
                setAppliedTerms(mapResponse(result));
                setWhyEdits(prev => ({ ...prev, [termId]: { explanation: '', confidenceScore: '', supportingPhrases: [] } }));
                setWhyOpen(prev => new Set([...prev, termId]));
            })
            .catch(err => toast.error(err.message || 'Failed to apply term'))
            .finally(() => setLoading(false));
    };

    const handleRemoveTerm = (termId: number): void => {
        setLoading(true);
        NewOccurrenceReportService.unlinkReportDescriptorTerms(occurrence.id, [termId])
            .then(() => {
                setAppliedTerms(prev => prev.filter(t => t.id !== termId));
                setWhyOpen(prev => { const n = new Set(prev); n.delete(termId); return n; });
            })
            .catch(err => toast.error(err.message || 'Failed to remove term'))
            .finally(() => setLoading(false));
    };

    const handleSaveWhy = (): void => {
        setLoading(true);
        NewOccurrenceReportService.linkReportDescriptorTerms(occurrence.id, buildPayload())
            .then(result => {
                setAppliedTerms(mapResponse(result));
                toast.success('Classification rationale saved');
            })
            .catch(err => toast.error(err.message || 'Failed to save rationale'))
            .finally(() => setLoading(false));
    };

    const updateWhyEdit = (termId: number, field: keyof WhyEdit, value: string | string[]): void => {
        setWhyEdits(prev => ({ ...prev, [termId]: { ...prev[termId], [field]: value } }));
    };

    const addPhrase = (termId: number): void => {
        const phrase = (phraseInputs[termId] || '').trim();
        if (!phrase) return;
        updateWhyEdit(termId, 'supportingPhrases', [...(whyEdits[termId]?.supportingPhrases || []), phrase]);
        setPhraseInputs(prev => ({ ...prev, [termId]: '' }));
    };

    const removePhrase = (termId: number, index: number): void => {
        updateWhyEdit(termId, 'supportingPhrases', (whyEdits[termId]?.supportingPhrases || []).filter((_, i) => i !== index));
    };

    const toggleAccordion = (_e: React.SyntheticEvent, id: number, isExpanded: boolean): void => {
        setExpandedSets(prev => {
            const next = new Set(prev);
            isExpanded ? next.add(id) : next.delete(id);
            return next;
        });
    };

    return (
        <>
            <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>

            {/* Applied terms summary */}
            <Paper
                elevation={0}
                sx={{ borderLeft: '4px solid', borderLeftColor: 'warning.main', bgcolor: 'warning.50', p: 2, mx: 2, mt: 2, mb: 3 }}
            >
                <Stack direction="row" alignItems="center" gap={1} mb={appliedGroups.length > 0 ? 1.5 : 0}>
                    <Typography variant="overline" sx={{ fontWeight: 600, color: 'warning.dark', lineHeight: 1 }}>
                        Applied Terms
                    </Typography>
                    <Chip
                        label={appliedTerms.length}
                        size="small"
                        color="warning"
                        sx={{ height: 18, fontSize: 11, fontWeight: 600, '.MuiChip-label': { px: 1 } }}
                    />
                </Stack>
                {appliedGroups.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No descriptors applied to this report yet.
                    </Typography>
                ) : (
                    <Stack gap={1}>
                        {appliedGroups.map(({ descriptor, terms }) => (
                            <Stack key={descriptor.id} direction="row" alignItems="flex-start" gap={1.5}>
                                <Typography
                                    variant="caption"
                                    sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.secondary', pt: '3px', minWidth: 160, flexShrink: 0 }}
                                >
                                    {descriptor.name}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                    {terms.map(t => (
                                        <Tooltip
                                            key={t.id}
                                            title={whyEdits[t.id!]?.explanation || ''}
                                            placement="top"
                                            arrow
                                            disableHoverListener={!whyEdits[t.id!]?.explanation}
                                        >
                                            <Chip
                                                label={t.term}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                onDelete={(): void => handleRemoveTerm(t.id!)}
                                                onMouseEnter={(): void => onPhrasesHover?.(whyEdits[t.id!]?.supportingPhrases ?? [])}
                                                onMouseLeave={(): void => onPhrasesHover?.([])}
                                            />
                                        </Tooltip>
                                    ))}
                                </Box>
                            </Stack>
                        ))}
                    </Stack>
                )}
            </Paper>

            {/* Descriptor set accordions */}
            <Box sx={{ px: 2, pb: 2 }}>
                {publishedDescriptors.map(descriptor => {
                    const nApplied = descriptor.descriptorTerms.filter(t => appliedSet.has(t.id)).length;
                    return (
                        <Accordion
                            key={descriptor.id}
                            expanded={expandedSets.has(descriptor.id)}
                            onChange={(e, isExpanded): void => toggleAccordion(e, descriptor.id, isExpanded)}
                            disableGutters
                            elevation={1}
                            sx={{ mb: 1, '&::before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, alignItems: 'flex-start' }}>
                                <Stack direction="row" alignItems="flex-start" gap={1.5} flex={1} mr={1}>
                                    <Box flex={1} minWidth={0}>
                                        <Typography variant="subtitle1" fontWeight={500}>{descriptor.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{descriptor.description}</Typography>
                                    </Box>
                                    <Chip
                                        label={nApplied > 0 ? `${nApplied} applied` : 'none applied'}
                                        size="small"
                                        color={nApplied > 0 ? 'success' : 'default'}
                                        variant={nApplied > 0 ? 'outlined' : 'filled'}
                                        sx={{ flexShrink: 0, alignSelf: 'flex-start', mt: 0.25 }}
                                    />
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                                {descriptor.descriptorTerms.map((term, index) => {
                                    const isApplied = appliedSet.has(term.id);
                                    const isWhyOpen = whyOpen.has(term.id!);
                                    const edit = whyEdits[term.id!] ?? { explanation: '', confidenceScore: '', supportingPhrases: [] };
                                    return (
                                        <Box key={term.id}>
                                            {index > 0 && <Divider />}

                                            {/* Term row */}
                                            <Stack
                                                direction="row"
                                                alignItems="flex-start"
                                                gap={1.5}
                                                px={2}
                                                py={1}
                                                sx={{ bgcolor: isApplied ? 'success.50' : 'transparent', transition: 'background-color 0.15s' }}
                                                onMouseEnter={isApplied ? (): void => onPhrasesHover?.(whyEdits[term.id!]?.supportingPhrases ?? []) : undefined}
                                                onMouseLeave={isApplied ? (): void => onPhrasesHover?.([]) : undefined}
                                            >
                                                <Switch
                                                    size="small"
                                                    color="success"
                                                    checked={isApplied}
                                                    onChange={(): void => isApplied ? handleRemoveTerm(term.id!) : handleApplyTerm(term.id!)}
                                                    onClick={(e): void => e.stopPropagation()}
                                                    sx={{ mt: 0.25, flexShrink: 0 }}
                                                />
                                                <Box flex={1} minWidth={0}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={isApplied ? 500 : 400}
                                                        color={isApplied ? 'success.dark' : 'text.primary'}
                                                    >
                                                        {term.term}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">{term.definition}</Typography>
                                                    {isApplied && !isWhyOpen && (
                                                        <Typography
                                                            variant="caption"
                                                            display="block"
                                                            mt={0.5}
                                                            sx={{ fontStyle: 'italic', color: edit.explanation ? 'success.dark' : 'text.disabled' }}
                                                        >
                                                            {edit.explanation || 'No rationale added'}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {isApplied && (
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        onClick={(): void => setWhyOpen(prev => {
                                                            const n = new Set(prev);
                                                            n.has(term.id!) ? n.delete(term.id!) : n.add(term.id!);
                                                            return n;
                                                        })}
                                                        sx={{ fontSize: 11, color: isWhyOpen ? 'success.main' : 'text.secondary', textTransform: 'none', minWidth: 'auto', flexShrink: 0, alignSelf: 'flex-start', mt: 0.5 }}
                                                    >
                                                        {isWhyOpen ? 'Close ▲' : 'Edit ▼'}
                                                    </Button>
                                                )}
                                            </Stack>

                                            {/* WHY panel */}
                                            {isApplied && isWhyOpen && (
                                                <Paper
                                                    elevation={0}
                                                    sx={{ bgcolor: 'success.50', borderTop: '1px solid', borderColor: 'success.200', px: 2, pt: 1.5, pb: 2, ml: 6 }}
                                                >
                                                    <Typography
                                                        variant="overline"
                                                        sx={{ display: 'block', color: 'success.dark', fontWeight: 600, mb: 1.5, letterSpacing: '0.1em' }}
                                                    >
                                                        Classification Rationale
                                                    </Typography>

                                                    <Stack gap={2}>
                                                        <TextField
                                                            label="Explanation"
                                                            size="small"
                                                            fullWidth
                                                            multiline
                                                            minRows={2}
                                                            placeholder="Why does this term apply to this report?"
                                                            value={edit.explanation}
                                                            onChange={(e): void => updateWhyEdit(term.id!, 'explanation', e.target.value)}
                                                        />

                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                                                Confidence Score: <strong>{edit.confidenceScore ? parseFloat(edit.confidenceScore).toFixed(2) : '—'}</strong>
                                                            </Typography>
                                                            <Slider
                                                                size="small"
                                                                min={0} max={1} step={0.01}
                                                                value={parseFloat(edit.confidenceScore) || 0}
                                                                onChange={(_e, val): void => updateWhyEdit(term.id!, 'confidenceScore', String(val as number))}
                                                                valueLabelDisplay="auto"
                                                                sx={{ color: 'success.main' }}
                                                            />
                                                        </Box>

                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                                                                Supporting Phrases
                                                            </Typography>
                                                            {edit.supportingPhrases.length > 0 && (
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                                                    {edit.supportingPhrases.map((phrase, i) => (
                                                                        <Chip
                                                                            key={i}
                                                                            label={phrase}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color="success"
                                                                            onDelete={(): void => removePhrase(term.id!, i)}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            )}
                                                            <Stack direction="row" gap={1}>
                                                                <TextField
                                                                    size="small"
                                                                    placeholder="Add supporting phrase, press Enter"
                                                                    value={phraseInputs[term.id!] || ''}
                                                                    onChange={(e): void => setPhraseInputs(prev => ({ ...prev, [term.id!]: e.target.value }))}
                                                                    onKeyDown={(e): void => { if (e.key === 'Enter') { e.preventDefault(); addPhrase(term.id!); } }}
                                                                    sx={{ flex: 1 }}
                                                                />
                                                                <Button variant="outlined" size="small" color="success" onClick={(): void => addPhrase(term.id!)}>
                                                                    Add
                                                                </Button>
                                                            </Stack>
                                                        </Box>
                                                    </Stack>

                                                    <Stack direction="row" justifyContent="space-between" mt={2} pt={1.5} sx={{ borderTop: '1px solid', borderColor: 'success.200' }}>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            variant="text"
                                                            onClick={(): void => handleRemoveTerm(term.id!)}
                                                        >
                                                            Remove from Report
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            color="success"
                                                            variant="contained"
                                                            disableElevation
                                                            onClick={handleSaveWhy}
                                                        >
                                                            Save Rationale
                                                        </Button>
                                                    </Stack>
                                                </Paper>
                                            )}
                                        </Box>
                                    );
                                })}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
        </>
    );
};

export default NewOccurrenceReportLinkedDescriptorsEditor;
